import { Node } from '@xyflow/react';
import NodeData, {
  NodeCategoryModel,
  IDhcpServerSettings,
  ERedundancyProtocol,
} from 'src/modules/network-configuration/models/node-data';
import { NodeModel } from 'src/modules/network-configuration/models/node-model';
import { generateNodeId } from 'src/modules/network-configuration/models/node-utils';
import { calculateNetworkAddress2 } from 'src/utils/ip';

import { buildNode } from './import-topology-shared';
import {
  toSwitchType,
  ImportContent,
  mergeIpInterfaces,
  isReservedAutoDeployVlan,
} from './import-topology-utils';

const getOptionValues = (
  options: { code: number; value?: unknown }[] | undefined,
  code: number,
): string[] => {
  const opt = (options ?? []).find((it) => it.code === code);
  const values = Array.isArray(opt?.value) ? opt.value : [];
  return values.filter((v): v is string => typeof v === 'string' && !!v);
};

const toDhcpServerSettings = (subnet?: {
  leasetime: number;
  pool_start: string;
  pool_end: string;
  options: { code: number; value?: unknown }[];
}): IDhcpServerSettings => {
  const routers = getOptionValues(subnet?.options, 3);
  const dns = getOptionValues(subnet?.options, 6);
  const domainName = getOptionValues(subnet?.options, 15)[0];
  const ntp = getOptionValues(subnet?.options, 42);
  const poolStart = subnet?.pool_start || '0.0.0.0';
  const poolEnd = subnet?.pool_end || '0.0.0.0';

  return {
    leaseTime: subnet?.leasetime || 86400,
    poolEnabled: poolStart !== '0.0.0.0' || poolEnd !== '0.0.0.0',
    poolStart,
    poolEnd,
    routerIp1: routers[0],
    routerIp2: routers[1],
    domainServerIp1: dns[0],
    domainServerIp2: dns[1],
    domainName,
    ntpServerIp1: ntp[0],
    ntpServerIp2: ntp[1],
  };
};

export const buildSwitchesFromCfg = (
  contents: ImportContent[],
): {
  switchNodes: Node<NodeModel>[];
  nodesByUniqueId: Map<string, string>;
} => {
  const nodesByUniqueId = new Map<string, string>();

  const switchNodes = contents.map((c, idx) => {
    const nodeType = toSwitchType(c.contentId);
    const category: NodeCategoryModel = JSON.parse(
      JSON.stringify(NodeData[nodeType]),
    );

    category.name =
      c.data.system?.cfgName ||
      c.contentName?.replace('.cfg', '') ||
      `Switch ${idx + 1}`;

    const bridges = (c.data.vlan?.bridges ?? []).filter(
      (b) => !isReservedAutoDeployVlan(b.vid),
    );
    const bridge0 = bridges.find((b) => b.vid === 0) ?? bridges[0];
    if (bridge0?.ip) {
      category.ip = bridge0.ip;
      category.subnetMask = bridge0.sm;
      category.subnet = calculateNetworkAddress2(bridge0.ip, bridge0.sm);
      category.gateway = bridge0.gw || '';
    }

    const vidModeByVid = new Map<number, string>();
    (c.data.dhcp?.vids ?? []).forEach((v) => {
      if (isReservedAutoDeployVlan(v.vid)) return;
      vidModeByVid.set(v.vid, v.type);
    });
    const subnetByNetwork = new Map(
      (c.data.dhcp?.subnets ?? []).map((s) => [s.ip, s]),
    );

    const ipInterfacesFromBridges = bridges
      .filter((b) => b.vid !== 0 && !!b.ip && !!b.sm)
      .map((b) => ({
        vlanId: b.vid,
        ip: b.ip,
        subnetMask: b.sm,
        subnet: calculateNetworkAddress2(b.ip, b.sm),
        dhcp: vidModeByVid.get(b.vid) === 'server',
        dhcpServer:
          vidModeByVid.get(b.vid) === 'server'
            ? toDhcpServerSettings(
                subnetByNetwork.get(calculateNetworkAddress2(b.ip, b.sm)),
              )
            : undefined,
      }));

    const vlanIdsOnSwitch = new Set<number>();
    (c.data.vlan?.vids ?? []).forEach((v) => {
      if (!isReservedAutoDeployVlan(v.vid)) vlanIdsOnSwitch.add(v.vid);
    });
    bridges.forEach((b) => {
      if (b.vid !== 0 && !isReservedAutoDeployVlan(b.vid))
        vlanIdsOnSwitch.add(b.vid);
    });
    (c.data.dhcp?.vids ?? []).forEach((v) => {
      if (v.vid !== 0 && !isReservedAutoDeployVlan(v.vid))
        vlanIdsOnSwitch.add(v.vid);
    });

    const merged = mergeIpInterfaces(ipInterfacesFromBridges, [
      ...vlanIdsOnSwitch,
    ]);
    if (merged.length) {
      category.ipInterfaces = merged.map((ipIf) => {
        const hasServer = vidModeByVid.get(ipIf.vlanId) === 'server';
        return {
          ...ipIf,
          dhcp: hasServer,
          dhcpServer: hasServer
            ? toDhcpServerSettings(subnetByNetwork.get(ipIf.subnet))
            : ipIf.dhcpServer,
        };
      });
    }

    if (vidModeByVid.get(0) === 'server') {
      category.dhcp = true;
      category.dhcpServer = toDhcpServerSettings(
        category.subnet ? subnetByNetwork.get(category.subnet) : undefined,
      );
    }
    const stpLan = c.data.stp?.stpVersion?.lan;
    const stpPriority = c.data.stp?.stpVersion?.lanprio;
    if (stpLan) {
      const redundancyProtocol =
        // eslint-disable-next-line no-nested-ternary
        stpLan === 'rstp'
          ? ERedundancyProtocol.RSTP
          : stpLan === 'none'
            ? ERedundancyProtocol.DISABLED
            : ERedundancyProtocol.AUTO;
      category.stp = {
        redundancyProtocol,
        bridgePriority:
          typeof stpPriority === 'number' && Number.isFinite(stpPriority)
            ? stpPriority
            : 8,
      };
    }

    const id = `${nodeType}-${generateNodeId()}`;
    nodesByUniqueId.set(c.uniqueId, id);
    return buildNode({
      id,
      nodeType,
      category,
      position: { x: 80 + idx * 360, y: 60 },
    });
  });

  return { switchNodes, nodesByUniqueId };
};
